import { settings, saveSettings } from "../settings";

/** A labeled range slider row that mutates a settings field live. */
function slider(
  label: string,
  min: number,
  max: number,
  step: number,
  value: number,
  apply: (v: number) => void,
  fmt: (v: number) => string,
  onChange: () => void,
): HTMLElement {
  const row = document.createElement("div");
  Object.assign(row.style, { display: "flex", flexDirection: "column", gap: "4px" });

  const head = document.createElement("div");
  Object.assign(head.style, { display: "flex", justifyContent: "space-between", fontSize: "13px" });
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.style.opacity = ".85";
  const val = document.createElement("span");
  val.textContent = fmt(value);
  val.style.fontFamily = "monospace";
  head.appendChild(lab);
  head.appendChild(val);

  const input = document.createElement("input");
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  Object.assign(input.style, { width: "100%", accentColor: "#2a9d8f" });
  input.addEventListener("input", () => {
    const v = Number(input.value);
    apply(v);
    val.textContent = fmt(v);
    saveSettings();
    onChange();
  });

  row.appendChild(head);
  row.appendChild(input);
  return row;
}

function button(text: string, primary: boolean, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = text;
  Object.assign(b.style, {
    padding: "10px", fontSize: "15px", fontWeight: "600", borderRadius: "6px", cursor: "pointer",
    border: primary ? "0" : "1px solid rgba(255,255,255,0.15)",
    background: primary ? "#2a9d8f" : "rgba(255,255,255,0.06)",
    color: primary ? "#06120f" : "#e9eef2",
  });
  b.addEventListener("click", onClick);
  return b;
}

/**
 * In-game pause overlay (Esc). The server keeps running — it's authoritative —
 * so "pause" means: free the cursor, show this overlay, and stop sending input
 * (the tank holds still server-side). Settings sliders apply live and persist.
 */
export class PauseMenu {
  readonly el: HTMLElement;

  constructor(onResume: () => void, onLeave: () => void, onChange: () => void) {
    this.el = document.createElement("div");
    Object.assign(this.el.style, {
      position: "fixed", inset: "0", display: "none",
      alignItems: "center", justifyContent: "center",
      background: "rgba(8,12,16,0.82)", color: "#e9eef2",
      fontFamily: "system-ui, sans-serif", zIndex: "50",
    });

    const card = document.createElement("div");
    Object.assign(card.style, {
      width: "340px", padding: "22px 26px", display: "flex", flexDirection: "column", gap: "14px",
      background: "rgba(14,20,26,0.96)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
    });

    const title = document.createElement("div");
    Object.assign(title.style, { fontSize: "24px", fontWeight: "700", letterSpacing: "2px", textAlign: "center" });
    title.textContent = "PAUSE";
    card.appendChild(title);

    // Sensibilité: higher aimRate = the turret/camera catch up to your aim
    // input faster (point somewhere, it turns there and settles — this
    // controls how briskly).
    card.appendChild(slider(
      "Sensibilité souris", 4, 20, 1, settings.aimRate,
      (v) => { settings.aimRate = v; }, (v) => v.toFixed(0), onChange,
    ));
    card.appendChild(slider(
      "Volume", 0, 100, 1, settings.masterVolume * 100,
      (v) => { settings.masterVolume = v / 100; }, (v) => `${v.toFixed(0)}%`, onChange,
    ));

    card.appendChild(button("Reprendre", true, onResume));
    card.appendChild(button("Quitter", false, onLeave));

    const credits = document.createElement("div");
    Object.assign(credits.style, { fontSize: "11px", opacity: ".45", textAlign: "center" });
    credits.innerHTML = `Dinosaure 3D par <a href="https://sketchfab.com/zixisun51" target="_blank" rel="noopener noreferrer" style="color:inherit">zixisun02</a> (Sketchfab)`;
    card.appendChild(credits);

    this.el.appendChild(card);
    document.body.appendChild(this.el);
  }

  show(): void {
    this.el.style.display = "flex";
  }

  hide(): void {
    this.el.style.display = "none";
  }
}
